import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Star, Award, Quote, Linkedin } from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { HEADLINE_STATS, PROCESS_STEPS, TESTIMONIALS } from '@/lib/content';

const DIFFS = [
  { title: 'Like an in-house team', body: 'A team of designers, programmers, and marketers who work as a direct extension of your business - same Slack, same goals, same accountability.' },
  { title: 'Transparent reporting', body: 'Real-time dashboards give you complete visibility. Know exactly where every marketing dollar goes and what it returns.' },
  { title: 'Results-focused', body: 'We focus on the metrics that matter - leads, conversions, and revenue. Vanity charts don\'t pay your bills.' },
  { title: 'Quick response', body: 'Questions answered within 24 hours. Most within two. Responsiveness is our greatest strength, not a slogan.' },
  { title: 'No long-term contracts', body: 'We earn your business every month. If we aren\'t delivering, you walk. Simple as that.' },
  { title: 'On-time & on-budget', body: 'Our team is incentivized to ship on-time and on-budget. No surprise invoices. No hidden fees.' },
];

const FOUNDER = {
  name: 'Ted Garber',
  role: 'Founder & CEO',
  img: '/images/ted-garber.jpg',
  bio: 'With more than 20 years in digital marketing, Ted founded Brand Partners on a simple principle: when your success is aligned with your clients\' success, you build relationships that last. He has led digital strategy for Disney, Ford, The Virgin Group, and Coldwell Banker, as well as thousands of small and mid-sized businesses nationwide.',
  link: 'https://www.linkedin.com/in/edward-garber-fl/',
};

const OFFICE_IMG = '/images/office-hero.png';
const IMG_FALLBACK = '/images/brand-partners-logo.png';

export default function WhyUsPage() {
  return (
    <main data-testid="why-us-page" className="bg-white">
      <SEO
        title="Why Brand Partners - 19 Years of Real Partnerships"
        description="Discover why 1k+ businesses partner with Brand Partners. 19 years, Google Certified, transparent reporting, no long-term contracts. We're the partner you keep."
        path="/why-us"
      />

      {/* HERO */}
      <section className="relative pt-14 lg:pt-20 pb-16 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal as="div" className="inline-flex items-center gap-2 bg-white border border-bp-slate-200 rounded-full px-3.5 py-1.5 shadow-sm">
              <Award size={14} className="text-bp-amber"/>
              <span className="text-[12.5px] font-semibold text-bp-slate">Voted Top 25 Agency · South Florida Business Journal</span>
            </Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-6 text-[48px] sm:text-[72px] lg:text-[84px]">
              The growth partner <br/>
              <span className="grad-text">your business deserves.</span>
            </Reveal>
            <Reveal as="p" delay={140} className="mt-6 text-[18px] text-bp-slate leading-relaxed max-w-2xl">
              Since 2007, we&rsquo;ve helped build over 1,000 websites through real partnerships, not vendor relationships.
              Working with Brand Partners is like having an in-house team - the processes, the structure, the delivery.
            </Reveal>
            <Reveal as="div" delay={200} className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="whyus-cta">Book a Strategy Call <ArrowRight size={16}/></Link>
            </Reveal>
          </div>
          <Reveal as="div" delay={160} className="lg:col-span-5">
            <div className="relative">
              <img
                src={OFFICE_IMG}
                alt="Brand Partners office - Ted Garber and team"
                className="w-full h-[440px] object-cover rounded-3xl shadow-xl"
                loading="lazy"
                onError={(e) => { if (e.currentTarget.src.indexOf(IMG_FALLBACK) === -1) { e.currentTarget.src = IMG_FALLBACK; e.currentTarget.classList.add('object-contain','bg-bp-slate-50','p-8'); } }}
              />
              <div className="card-floating absolute -bottom-6 -left-6 p-5 max-w-[220px] hidden sm:block">
                <div className="flex gap-1">
                  {[0,1,2,3,4].map((i)=><Star key={i} size={14} className="text-bp-amber fill-bp-amber"/>)}
                </div>
                <div className="font-display font-extrabold text-[26px] grad-text mt-2 leading-none">5.0</div>
                <div className="text-[12px] text-bp-slate-soft mt-1">21 Google Reviews · Verified</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-14 bg-white border-y border-bp-slate-200">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {HEADLINE_STATS.map((s, i) => (
            <Reveal as="div" key={s.label} delay={i * 80} className="text-center lg:text-left">
              <div className="font-display font-extrabold text-[44px] sm:text-[56px] leading-none tracking-tight grad-text">{s.value}{s.suffix}</div>
              <div className="text-[12.5px] font-medium text-bp-slate-soft mt-2 uppercase tracking-wider">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="py-20 lg:py-28 bg-bp-slate-50">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <Reveal as="div" className="kicker"><span className="dot"/>What makes us different</Reveal>
              <Reveal as="h2" delay={80} className="h-display mt-3 text-[40px] sm:text-[54px]">
                Your success is <span className="grad-text">how we measure ours.</span>
              </Reveal>
            </div>
            <Reveal as="p" delay={120} className="lg:col-span-5 text-[17px] text-bp-slate leading-relaxed">
              We align our efforts with your long-term goals - because the only growth that matters is the growth that keeps growing.
            </Reveal>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DIFFS.map((d, i) => (
              <Reveal as="article" key={d.title} delay={i * 70} className="card-soft p-7">
                <CheckCircle2 size={22} className="text-bp-teal"/>
                <h3 className="font-display font-bold text-[20px] text-bp-ink mt-4">{d.title}</h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-bp-slate">{d.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <Reveal as="div" className="kicker"><span className="dot"/>How we work with you</Reveal>
              <Reveal as="h2" delay={80} className="h-display mt-3 text-[40px] sm:text-[54px]">
                A proven process. <span className="grad-text">Refined over 19 years.</span>
              </Reveal>
            </div>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal as="div" key={s.n} delay={i * 80} className="card-soft p-6 lg:p-7">
                <div className="font-display font-extrabold text-[44px] leading-none grad-text">{s.n}</div>
                <h3 className="font-display font-bold text-[20px] text-bp-ink mt-5">{s.title}</h3>
                <p className="text-[14.5px] text-bp-slate mt-2.5 leading-relaxed">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER + TEAM */}
      <section className="py-20 lg:py-28 bg-bp-ink text-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal as="div" className="lg:col-span-4">
            <img
              src={FOUNDER.img}
              alt={FOUNDER.name}
              className="w-full h-auto rounded-3xl"
              loading="lazy"
              onError={(e) => { if (e.currentTarget.src.indexOf(IMG_FALLBACK) === -1) { e.currentTarget.src = IMG_FALLBACK; e.currentTarget.classList.add('object-contain','bg-bp-slate-50','p-8'); } }}
            />
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-6">
            <div className="kicker text-bp-teal"><span className="dot" style={{background:'#FB6A48'}}/>Our team</div>
            <h2 className="font-display font-extrabold text-[40px] sm:text-[54px] leading-[1.02] mt-3">
              Meet the people behind your <span className="grad-text">growth.</span>
            </h2>
            <div className="mt-8">
              <div className="font-display font-bold text-2xl">{FOUNDER.name}</div>
              <div className="text-[13px] text-bp-teal font-semibold uppercase tracking-wider mt-1">{FOUNDER.role}</div>
              <p className="mt-4 text-[15.5px] leading-relaxed text-white/85 max-w-2xl">{FOUNDER.bio}</p>
              <a href={FOUNDER.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-5 text-[14px] font-semibold text-bp-teal hover:text-white" data-testid="founder-linkedin">
                <Linkedin size={16}/> Connect on LinkedIn
              </a>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {[
                { name: 'Strategy', sub: 'Account Strategists', body: 'Dedicated managers who become an extension of your team.' },
                { name: 'Creative', sub: 'Design & Development', body: 'Award-winning designers and engineers building conversion-grade sites.' },
                { name: 'Performance', sub: 'SEO & Paid', body: 'Specialists who put you at the top of search and maximize ad spend.' },
              ].map((t) => (
                <div key={t.name} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                  <h3 className="font-display font-bold">{t.name}</h3>
                  <div className="text-[11.5px] font-semibold uppercase tracking-wider text-bp-teal mt-1">{t.sub}</div>
                  <p className="text-[13.5px] text-white/75 mt-2.5 leading-relaxed">{t.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="kicker"><span className="dot" style={{background:'#F59E0B'}}/>5.0 · 21 Google Reviews</div>
          <h2 className="h-display mt-3 text-[40px] sm:text-[54px] max-w-3xl">What clients <span className="grad-text">actually say.</span></h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.slice(0,3).map((t, i) => (
              <Reveal as="figure" key={t.author} delay={i * 80} className="card-soft p-7">
                <Quote size={22} className="text-bp-blue/30"/>
                <blockquote className="mt-3 text-[15px] leading-relaxed text-bp-slate">{t.quote}</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-bp-slate-200">
                  <div className="font-display font-bold text-bp-ink">{t.author}</div>
                  <div className="text-[12.5px] text-bp-slate-soft mt-0.5">{t.role}</div>
                </figcaption>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
